import type { ReactNode } from 'react';

/**
 * The minimal shape we need from a tool reference: a backticked
 * prompt-ready wire name string (e.g. `` `queryKnowledgeGraph` ``).
 * Call sites read these from `<ToolsContext>` —
 * `tool={tools.queryKnowledgeGraph}` — so per-agent renames flow
 * through automatically.
 */
export type ToolMention = string;

interface SketchProps {
  /**
   * Optional heading rendered as `<h4>` above the ordered list.
   * Sketches placed inside a catalog entry's body share the entry's
   * `.Def` heading, so they typically don't need one.
   */
  heading?: ReactNode;
  children: ReactNode;
}

/**
 * A numbered list of `<Step>` items. Wraps children in `<ol>` so the
 * reconciler emits a markdown numbered list.
 */
export function Sketch({ heading, children }: SketchProps) {
  return (
    <>
      {heading == null ? null : <h4>{heading}</h4>}
      <ol>{children}</ol>
    </>
  );
}

interface StepProps {
  /**
   * The tool the step invokes — a backticked wire name string read
   * from `<ToolsContext>` (e.g. `tools.queryKnowledgeGraph`).
   * Per-agent renaming flows through automatically.
   */
  tool?: ToolMention;
  /**
   * Optional lead-in connective rendered before the tool name —
   * "Apply the gate; then ...". May contain mentions (e.g. a
   * `<Foo.Ref/>` of another catalog entry).
   */
  lead?: ReactNode;
  /**
   * Optional input binding(s) the tool consumes — rendered as
   * `` (`from`) ``. Emitted as a text node with literal backticks
   * rather than a `<code>` element: the reconciler injects a synthetic
   * space at element-text boundaries (so `(<code>ws</code>)` would
   * render as `( \`ws\`)`), while text-text adjacency keeps the
   * parenthesis glued.
   */
  from?: string;
  /**
   * Optional output binding the tool produces — rendered as
   * `` → `to` ``. Same text-node convention as `from`.
   */
  to?: string;
  /**
   * Trailing parenthetical note rendered after children. Use for short
   * remarks that don't warrant a separate paragraph.
   */
  note?: ReactNode;
  /**
   * Step body — typically a single `<Expr>` with the s-expression for
   * this step. May be empty for pure-reference steps that defer to a
   * mentioned catalog entry.
   */
  children?: ReactNode;
}

/**
 * One step of a sketch: a single tool invocation with optional
 * input/output bindings, lead-in, expression body, and trailing note.
 *
 * Rendering shape (one `<li>` per step):
 *   ${lead}; then `tool` (from) → `to`: <Expr> (note)
 *   ${lead}; then `tool` (from) → `to`. (note)   ← when no Expr
 *
 * The `:` vs `.` punctuation distinguishes "code follows" from "this is
 * a pure directive". Authors don't choose it — presence of children
 * (the `<Expr>` body) does.
 */
export function Step({ tool, lead, from, to, note, children }: StepProps) {
  const hasBody = children !== undefined && children !== null && children !== false;
  return (
    <li>
      {lead == null ? null : <>{lead}; then </>}
      {tool}
      {from == null ? null : ` (\`${from}\`)`}
      {to == null ? null : ` → \`${to}\``}
      {hasBody ? ':' : '.'}
      {children}
      {note == null ? null : <> ({note})</>}
    </li>
  );
}
